import { useEffect, useMemo, useRef, useState } from "react";
import { useCan } from "../../hooks/useAreaLevel";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import { useTheme } from "../../context/ThemeContext";
import {
  ChevronLeft, ChevronDown, ChevronRight, Phone, Mail, MapPin, Crown, ShieldCheck, Users2, Package2, PhoneCall,
  FileText, StickyNote, Activity, TrendingUp, Copy, ExternalLink, Building2,
  Plus, X, Loader2, CalendarDays, Mail as MailIcon, MessageSquare,
  Paperclip, Download, RefreshCw,
  Briefcase, Globe, Landmark, CreditCard, Linkedin, Facebook as FacebookIcon, Instagram as InstagramIcon,
  Pencil, Check, Sparkles, ArrowLeft, MessageCircle, Smartphone, Tag, Hash, Database, RotateCcw,
} from "lucide-react";
import PipedriveIdChip from "../../components/PipedriveIdChip";
import MediaManager from "../../components/MediaManager";
import { OwnerAssignChip } from "../../components/OwnerAssignChip";
import SyncButton from "../../components/SyncButton";
import { OrderDetailModal } from "../orders/OrdersDashboard";
import { useAuth } from "../../context/AuthContext";
import { TIER_STYLES } from "../../constants/pipeline";
import ActivityLogModal from "../../components/activity/ActivityLogModal";
import QuoteNotesPeek from "../../components/crm/QuoteNotesPeek";
import { TradeAccountCard, TradeAccountBadge } from "../../components/crm/TradeAccount";
import AMTouchesCard from "../../components/crm/AMTouchesCard";
import OldQuotesCard from "../../components/crm/OldQuotesCard";
import QuotePeekModal from "../../components/QuotePeekModal";
import AddressBookPanel from "../../components/addresses/AddressBookPanel";
import { useAddressBook, addressOneLine } from "../../lib/addressBook";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ─── shared helpers ─────────────────────────────────────────────────────
function TierChip({ tier }) {
  if (!tier) return null;
  const key = String(tier).toUpperCase().split(/[\s-]/)[0];
  const s = TIER_STYLES[key] || TIER_STYLES.DEFAULT;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-widest border ${s.bg} ${s.text} ${s.border}`}
      data-testid={`crm360-tier-${key}`}>
      <Icon className="w-3 h-3" /> {tier}
    </span>
  );
}
function money(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return "£" + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "—";
  return d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function fmtDay(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "—";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}
function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "";
  const days = Math.round((Date.now() - +d) / 86400000);
  if (days === 0)   return "today";
  if (days === 1)   return "yesterday";
  if (days < 30)    return `${days}d ago`;
  if (days < 365)   return `${Math.round(days / 30)}mo ago`;
  return `${Math.round(days / 365)}y ago`;
}

// Strip HTML tags for note previews (Pipedrive notes contain <br />).
function textOnly(s) {
  if (!s) return "";
  return String(s).replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "").trim();
}

// Source badge — makes it clear where each event came from.
const SOURCE_STYLES = {
  pipedrive:   { label: "Pipedrive",  cls: "bg-purple-500/15 text-purple-300 border-purple-500/40" },
  bigcommerce: { label: "BC",         cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  justcall:    { label: "JustCall",   cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  talmia:      { label: "Talmia",     cls: "bg-orange-500/15 text-orange-300 border-orange-500/40" },
  whatsapp:    { label: "WhatsApp",   cls: "bg-green-500/15 text-green-300 border-green-500/40" },
};
function SourceBadge({ source }) {
  const s = SOURCE_STYLES[source] || { label: source, cls: "bg-white/5 text-slate-300 border-white/10" };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-widest border ${s.cls}`}>
      {s.label}
    </span>
  );
}

// Timeline channel chips (spec §4 / §4A). Curated coloured row: the count-chips
// (Orders/Quotes/Calls/Files/Notes) folded together with Email + an "Other"
// catch-all (activities / WhatsApp / misc). 0-count channels auto-hide.
const TL_CHANNELS = [
  { key: "order", label: "Orders" },
  { key: "quote", label: "Quotes" },
  { key: "call", label: "Calls" },
  { key: "email", label: "Email" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "note", label: "Notes" },
  { key: "attachment", label: "Files" },
  { key: "other", label: "Other" },
];

// Map a raw event `type` → the curated chip channel it belongs to.
const CHANNEL_OF = {
  order: "order", quote: "quote", call: "call", email: "email",
  note: "note", attachment: "attachment", activity: "other", whatsapp: "whatsapp",
};

// Type meta — icon + colour per timeline event type.
const TYPE_META = {
  activity:   { icon: Activity,   ring: "ring-purple-500/40",  bg: "bg-purple-500/10",  iconColor: "text-purple-300"  },
  note:       { icon: StickyNote, ring: "ring-slate-500/40",   bg: "bg-slate-500/10",   iconColor: "text-slate-300"   },
  order:      { icon: Package2,   ring: "ring-emerald-500/40", bg: "bg-emerald-500/10", iconColor: "text-emerald-300" },
  call:       { icon: PhoneCall,  ring: "ring-sky-500/40",     bg: "bg-sky-500/10",     iconColor: "text-sky-300"     },
  quote:      { icon: FileText,   ring: "ring-orange-500/40",  bg: "bg-orange-500/10",  iconColor: "text-orange-300"  },
  attachment: { icon: Paperclip,  ring: "ring-fuchsia-500/40", bg: "bg-fuchsia-500/10", iconColor: "text-fuchsia-300" },
  email:      { icon: MailIcon,   ring: "ring-blue-500/40",    bg: "bg-blue-500/10",    iconColor: "text-blue-300"    },
  whatsapp:   { icon: MessageCircle, ring: "ring-green-500/40", bg: "bg-green-500/10",  iconColor: "text-green-300"   },
  other:      { icon: MessageSquare, ring: "ring-slate-500/40",  bg: "bg-slate-500/10",  iconColor: "text-slate-300"   },
};

// Human-readable file size.
function fmtBytes(n) {
  if (n === null || n === undefined) return null;
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return null;
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = num;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

// ─── timeline builder ─────────────────────────────────────────────────────
function buildTimeline(person, orders, calls, quotes, talmiaActivities = [], talmiaNotes = [], emails = []) {
  const ev = [];
  for (const a of person.activities || []) {
    ev.push({
      key: `a-${a.PDActivityId || a.Subject}-${a.CreatedDate || a.DueDate}`,
      type: "activity",
      source: "pipedrive",
      timestamp: a.CreatedDate || a.DueDate,
      title: a.Subject || a.Type || "Activity",
      body: a.Content,
      meta: [
        a.Type && `Type: ${a.Type}`,
        a.DueDate && `Due: ${fmtDate(a.DueDate)}`,
        a.UserName && `By: ${a.UserName}`,
      ].filter(Boolean),
    });
  }
  for (const n of person.notes || []) {
    ev.push({
      key: `n-${n.CreatedDate}-${(n.Content || "").slice(0, 20)}`,
      type: "note",
      source: "pipedrive",
      timestamp: n.CreatedDate,
      title: "Note",
      body: n.Content,
      meta: n.UserName ? [`By: ${n.UserName}`] : [],
    });
  }
  for (const f of person.attachments || []) {
    const size = fmtBytes(f.FileSize || f.file_size);
    ev.push({
      key: `f-${f.PDFileId || f.Id || f.Name || f.name}-${f.AddTime || f.add_time || ""}`,
      type: "attachment",
      source: "pipedrive",
      timestamp: f.AddTime || f.add_time || f.CreatedDate,
      title: f.Name || f.file_name || f.name || "Attachment",
      body: f.Description || f.description || null,
      meta: [
        (f.FileType || f.file_type) && `Type: ${f.FileType || f.file_type}`,
        size && `Size: ${size}`,
        (f.UserName || f.user_name) && `By: ${f.UserName || f.user_name}`,
      ].filter(Boolean),
      linkTo: f.Url || f.url || null,
      external: true,
    });
  }
  for (const a of talmiaActivities || []) {
    const due = a.due_date ? `${fmtDate(a.due_date)}${a.due_time ? ` ${a.due_time}` : ""}` : null;
    const aStatus = a.status || (a.completed ? "done" : "open");
    ev.push({
      key: `ta-${a._id}`,
      type: "activity",
      source: "talmia",
      timestamp: a.created_at,
      title: a.subject,
      body: a.content,
      activityId: a._id,
      activityStatus: aStatus,
      activityDue: a.due_date || null,
      meta: [
        a.activity_type && `Type: ${a.activity_type}`,
        due && `Due: ${due}`,
        a.priority && `Priority: ${a.priority}`,
        a.assignee_name && `For: ${a.assignee_name}`,
        a.created_by_name && `By: ${a.created_by_name}`,
        a.ai_assisted && "AI-assisted",
        aStatus === "done" ? "Done" : aStatus === "cancelled" ? "Cancelled" : (a.workspace_task_id ? "Task created" : "Open"),
      ].filter(Boolean),
    });
  }
  for (const m of emails || []) {
    const isWa = m.source === "whatsapp";
    const dirLabel = m.direction === "outbound" ? "Sent" : m.direction === "inbound" ? "Received" : null;
    ev.push({
      key: `em-${m._id}`,
      emailId: m._id,
      type: isWa ? "whatsapp" : "email",
      source: isWa ? "whatsapp" : "talmia",
      timestamp: m.sent_at || m.created_at,
      title: m.subject || (isWa ? "WhatsApp message" : "Email"),
      body: m.snippet || m.body_text || m.body || null,
      preview: m.preview || m.snippet || "",
      inboxName: m.inbox_name || null,
      direction: m.direction,
      phoneKey: isWa ? (m.phone_key || null) : null,
      messageId: m.message_id || null,
      meta: [
        isWa ? dirLabel : (m.direction && `${m.direction}`),
        m.inbox_name && !isWa && `Inbox: ${m.inbox_name}`,
        m.from && `From: ${m.from}`,
        m.to && `To: ${m.to}`,
      ].filter(Boolean),
    });
  }
  for (const n of talmiaNotes || []) {
    ev.push({
      key: `tn-${n._id}`,
      type: "note",
      source: "talmia",
      timestamp: n.created_at,
      title: "Note",
      body: n.content,
      meta: n.created_by_name ? [`By: ${n.created_by_name}`] : [],
    });
  }
  for (const o of orders || []) {
    ev.push({
      key: `o-${o.id}`,
      type: "order",
      source: "bigcommerce",
      timestamp: o.date_created,
      title: `Order #${o.bc_order_id}`,
      body: null,
      emailMatch: o.email_match,
      meta: [
        o.total_inc_tax && `Total: ${money(o.total_inc_tax)}`,
        o.status && `Status: ${o.status}`,
        o.items_count && `${o.items_count} items`,
        o.supplier && `Supplier: ${o.supplier}`,
        o.email_match === false && "⚠ billing email differs from customer",
      ].filter(Boolean),
      linkTo: `/lab/orders/${o.id}`,
    });
  }
  for (const c of calls || []) {
    ev.push({
      key: `c-${c.id}`,
      type: "call",
      source: "justcall",
      timestamp: c.created_date,
      title: c.summary || `${c.direction || ""} call · ${c.category || "Uncategorised"}`.trim(),
      body: c.notes,
      meta: [
        c.direction && c.direction,
        c.duration && `${c.duration}s`,
        c.sentiment && `Sentiment: ${c.sentiment}`,
        c.score != null && `Score: ${c.score}`,
      ].filter(Boolean),
      linkTo: `/lab/calls/${c.id}`,
    });
  }
  for (const q of quotes || []) {
    ev.push({
      key: `q-${q.id}`,
      type: "quote",
      source: "talmia",
      timestamp: q.created_date,
      title: `Quote ${q.quote_ref || q.id}${q.project ? ` · ${q.project}` : ""}`,
      body: null,
      linkTo: `/quotes/${q.id}/edit`,
      quoteId: q.id,
      quoteRef: q.quote_ref || q.id,
      quoteNotes: q.staff_notes || [],
      meta: [
        q.project && `Project: ${q.project}`,
        q.staff_notes?.length > 0 && `Notes: ${q.staff_notes.length}`,
        q.total != null && `Total: ${money(q.total)}`,
        q.status && `Status: ${q.status}`,
        q.account_manager && `AM: ${q.account_manager}`,
      ].filter(Boolean),
    });
  }
  // Collapse true duplicates (source data repeats some activities) using a
  // content signature, and guarantee unique React keys. Then sort newest-first.
  const seenSig = new Set();
  const seenKey = new Set();
  const deduped = [];
  for (const e of ev) {
    const sig = `${e.type}|${e.timestamp || ""}|${(e.title || "").trim()}`;
    if (seenSig.has(sig)) continue;   // drop the duplicate row
    seenSig.add(sig);
    let k = e.key || sig;
    while (seenKey.has(k)) k = `${k}-${seenKey.size}`;
    seenKey.add(k);
    deduped.push({ ...e, key: k });
  }
  return deduped.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
}

// ─── UI: email direction colour helper (shared by row + drawer) ───────────
const EMAIL_DIR = {
  outbound: { pill: "bg-orange-500/15 text-orange-300", text: "text-orange-300", preview: "text-orange-300/70", label: "↗ Sent",
              bubble: "bg-orange-500/10 border-orange-500/30", bubbleText: "text-orange-200", align: "justify-end", corner: "rounded-br-sm" },
  inbound:  { pill: "bg-teal-500/15 text-teal-300", text: "text-teal-300", preview: "text-teal-300/70", label: "↙ Received",
              bubble: "bg-teal-500/10 border-teal-500/30", bubbleText: "text-teal-200", align: "justify-start", corner: "rounded-bl-sm" },
  neutral:  { pill: "bg-slate-500/15 text-slate-300", text: "text-slate-200", preview: "text-slate-500", label: "• Email",
              bubble: "bg-slate-600/10 border-slate-500/30", bubbleText: "text-slate-200", align: "justify-center", corner: "rounded-b-sm" },
};
const emailDir = (d) => EMAIL_DIR[d === "outbound" ? "outbound" : d === "inbound" ? "inbound" : "neutral"];

// ─── UI: compact single-line email row in the timeline (spec A.2) ─────────
function EmailTimelineRow({ ev, onOpen }) {
  const d = emailDir(ev.direction);
  return (
    <div className="flex gap-3" data-testid="crm360-timeline-email">
      <div className="w-8 h-8 rounded-full bg-blue-500/10 ring-1 ring-blue-500/40 grid place-items-center shrink-0">
        <MailIcon className="w-4 h-4 text-blue-300" />
      </div>
      <button
        type="button"
        onClick={() => onOpen(ev)}
        className="flex-1 min-w-0 pb-4 border-b border-white/[0.04] last:border-0 text-left group"
        data-testid={`crm360-email-row-${ev.emailId}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full shrink-0 ${d.pill}`} data-testid="crm360-email-direction">
            {d.label}
          </span>
          <span className={`text-sm font-medium truncate ${d.text}`}>{ev.title}</span>
          {ev.preview && <span className={`text-[11.5px] truncate hidden sm:inline ${d.preview}`}>· {ev.preview}</span>}
          <span className="text-[11px] text-slate-500 font-mono whitespace-nowrap shrink-0 ml-auto">{relTime(ev.timestamp)}</span>
        </div>
        {ev.preview && (
          <div className={`text-[11.5px] mt-0.5 truncate pl-1 sm:hidden ${d.preview}`}>{ev.preview}</div>
        )}
      </button>
    </div>
  );
}

// ─── UI: "N more emails · View conversation" collapse row (spec A.3) ──────
function EmailMoreRow({ count, onOpen }) {
  return (
    <div className="flex gap-3" data-testid="crm360-email-more-row">
      <div className="w-8 h-8 rounded-full bg-blue-500/5 ring-1 ring-blue-500/20 grid place-items-center shrink-0">
        <MailIcon className="w-4 h-4 text-blue-300/60" />
      </div>
      <button
        type="button"
        onClick={onOpen}
        className="flex-1 min-w-0 pb-4 border-b border-white/[0.04] last:border-0 text-left text-[12px] text-blue-300 hover:text-blue-200 font-mono inline-flex items-center gap-1.5"
        data-testid="crm360-email-view-conversation"
      >
        {count} more email{count === 1 ? "" : "s"} · View conversation <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─── UI: email conversation drawer — all emails as chat bubbles (spec A.4) ─
function EmailBubble({ m, hot }) {
  const [expanded, setExpanded] = useState(false);
  const d = emailDir(m.direction);
  const body = (m.body || m.body_text || "").trim();
  const long = body.split("\n").length > 6 || body.length > 420;
  const shown = expanded || !long ? body : body.split("\n").slice(0, 6).join("\n").slice(0, 420);
  return (
    <div
      ref={hot ? (el) => el && el.scrollIntoView({ block: "center" }) : undefined}
      className={`flex ${d.align}`}
    >
      <div className={`max-w-[85%] rounded-2xl px-3 py-2 border ${d.bubble} ${d.corner} ${hot ? "ring-2 ring-blue-400/70" : ""}`}
        data-testid={hot ? "crm360-email-bubble-highlight" : "crm360-email-bubble"}
      >
        <div className={`flex items-center gap-1.5 mb-0.5 text-[10px] font-mono uppercase tracking-widest ${d.text}`}>
          {d.label}
          {m.inbox_name && <span className="text-slate-500 normal-case tracking-normal">· {m.inbox_name}</span>}
          <span className="text-slate-500 normal-case tracking-normal">· {fmtDate(m.sent_at)}</span>
        </div>
        {m.subject && <div className={`text-sm font-bold ${d.bubbleText}`}>{m.subject}</div>}
        {body && (
          <div className={`text-sm whitespace-pre-wrap break-words mt-0.5 ${d.bubbleText}`}>
            {shown}{long && !expanded && "…"}
          </div>
        )}
        {long && (
          <button type="button" onClick={() => setExpanded((v) => !v)}
            className="mt-1 text-[11px] text-slate-400 hover:text-white font-mono"
            data-testid="crm360-email-show-more">
            {expanded ? "Show less" : "Show more"}
          </button>
        )}
      </div>
    </div>
  );
}

function EmailThreadDrawer({ emails, highlightId, contactName, onBack }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onBack(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onBack]);

  // Oldest → newest, with per-day separators.
  const sorted = [...(emails || [])].sort((a, b) => new Date(a.sent_at || 0) - new Date(b.sent_at || 0));
  let lastDay = null;

  return createPortal(
    <div
      className="fixed inset-0 z-[150] flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onBack(); }}
      data-testid="crm360-email-thread"
    >
      <div className="w-full max-w-2xl my-8 rounded-2xl border border-blue-500/20 bg-[#0E1524] shadow-2xl shadow-black/60 overflow-hidden">
        <div className="bg-[#0B111C] border-b border-white/10 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <MailIcon className="w-4 h-4 text-blue-300 shrink-0" />
            <div className="min-w-0">
              <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono">Email conversation · {sorted.length}</div>
              <div className="text-sm font-display font-semibold text-white truncate">{contactName || "Contact"}</div>
            </div>
          </div>
          <button type="button" onClick={onBack} className="text-slate-400 hover:text-white" aria-label="Close" data-testid="crm360-email-thread-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-2 max-h-[72vh] overflow-y-auto">
          {sorted.length === 0 && <div className="py-6 text-center text-sm text-slate-500">No emails.</div>}
          {sorted.map((m) => {
            const day = m.sent_at ? new Date(m.sent_at).toDateString() : "—";
            const showSep = day !== lastDay;
            lastDay = day;
            return (
              <div key={m._id || m.message_id}>
                {showSep && (
                  <div className="flex items-center gap-2 my-3" data-testid="crm360-email-day-sep">
                    <div className="h-px flex-1 bg-white/[0.06]" />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                      {m.sent_at ? new Date(m.sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "Unknown date"}
                    </span>
                    <div className="h-px flex-1 bg-white/[0.06]" />
                  </div>
                )}
                <EmailBubble m={m} hot={highlightId && (m._id === highlightId)} />
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── UI: TimelineEvent (collapsible per-event row · spec §4A) ──────────────
function TimelineEvent({ ev, onDrill, onViewWhatsApp, openSignal, onActivityStatus }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (openSignal && typeof openSignal.value === "boolean") setOpen(openSignal.value);
  }, [openSignal]);
  const meta = TYPE_META[ev.type] || TYPE_META.note;
  const Icon = meta.icon;
  const body = (ev.type === "note" || ev.type === "activity") ? textOnly(ev.body) : ev.body;
  const canDrill = onDrill && ["order", "call", "quote"].includes(ev.type);
  const bodyStr = body ? String(body).trim() : "";
  const hasDetail = !!bodyStr || (ev.meta?.length > 0) || !!ev.linkTo;
  const peek = bodyStr
    ? bodyStr.replace(/\s+/g, " ").slice(0, 96)
    : (ev.meta?.slice(0, 2).join(" · ") || "");
  return (
    <div className="flex gap-3" data-testid={`crm360-timeline-${ev.type}`}>
      <div className={`w-8 h-8 rounded-full ${meta.bg} ring-1 ${meta.ring} grid place-items-center shrink-0`}>
        <Icon className={`w-4 h-4 ${meta.iconColor}`} />
      </div>
      <div className="flex-1 min-w-0 pb-4 border-b border-white/[0.04] last:border-0">
        {/* Header row — click anywhere to expand/collapse the event */}
        <button
          type="button"
          onClick={() => hasDetail && setOpen((v) => !v)}
          className={`w-full text-left group ${hasDetail ? "cursor-pointer" : "cursor-default"}`}
          data-testid={`crm360-timeline-toggle-${ev.type}`}
          aria-expanded={open}
        >
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0 flex items-center gap-1.5">
              {hasDetail && (
                <ChevronRight className={`w-3.5 h-3.5 text-slate-500 shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
              )}
              <span className={`text-sm font-medium truncate ${ev.type === "whatsapp" && ev.direction ? (ev.direction === "outbound" ? "text-orange-300" : "text-teal-300") : "text-white"}`}>{ev.title}</span>
              <SourceBadge source={ev.source} />
              {ev.type === "activity" && ev.activityStatus && (() => {
                const overdue = ev.activityStatus === "open" && ev.activityDue && String(ev.activityDue) < new Date().toISOString().slice(0, 10);
                const label = overdue ? "Overdue" : ev.activityStatus;
                const cls = overdue ? "bg-rose-500/15 text-rose-300"
                  : ev.activityStatus === "done" ? "bg-emerald-500/15 text-emerald-300"
                  : ev.activityStatus === "cancelled" ? "bg-slate-500/15 text-slate-400"
                  : "bg-amber-500/15 text-amber-300";
                return <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full shrink-0 ${cls}`} data-testid={`crm360-tl-act-status-${ev.activityId}`}>{label}</span>;
              })()}
              {ev.type === "whatsapp" && ev.direction && (
                <span
                  className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full shrink-0 ${ev.direction === "outbound" ? "bg-orange-500/15 text-orange-300" : "bg-teal-500/15 text-teal-300"}`}
                  data-testid="crm360-wa-direction"
                >
                  {ev.direction === "outbound" ? "↗ Sent" : "↙ Received"}
                </span>
              )}
              {ev.type === "order" && ev.emailMatch === false && (
                <span
                  className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full shrink-0 bg-amber-500/15 text-amber-300"
                  title="The order's billing email differs from this customer's email"
                  data-testid="crm360-order-email-differs"
                >
                  email differs
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 font-mono whitespace-nowrap shrink-0">
              {fmtDate(ev.timestamp)}
              <span className="text-slate-600"> · {relTime(ev.timestamp)}</span>
            </div>
          </div>
          {!open && peek && (
            <div className={`text-[11.5px] mt-0.5 truncate pl-5 ${ev.type === "whatsapp" && ev.direction ? (ev.direction === "outbound" ? "text-orange-300/80" : "text-teal-300/80") : "text-slate-500"}`} data-testid={`crm360-timeline-peek-${ev.type}`}>
              {peek}{bodyStr.length > 96 ? "…" : ""}
            </div>
          )}
        </button>

        {/* Expanded detail — meta + full body + drill/link */}
        {open && (
          <div className="mt-2 pl-5 space-y-1.5" data-testid={`crm360-timeline-detail-${ev.type}`}>
            {ev.meta?.length > 0 && (
              <div className="text-[11px] text-slate-500 font-mono flex flex-wrap gap-x-3 gap-y-0.5">
                {ev.meta.map((m, i) => <span key={`meta-${i}-${String(m).slice(0, 16)}`}>{m}</span>)}
              </div>
            )}
            {ev.type === "activity" && ev.activityId && onActivityStatus && (
              <div className="flex items-center gap-1.5 pt-0.5" data-testid={`crm360-tl-act-actions-${ev.activityId}`}>
                {ev.activityStatus === "open" ? (
                  <>
                    <button type="button" onClick={() => onActivityStatus(ev.activityId, "done")}
                      data-testid={`crm360-tl-act-done-${ev.activityId}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10.5px] hover:bg-emerald-500/20"><Check className="w-3 h-3" /> Done</button>
                    <button type="button" onClick={() => onActivityStatus(ev.activityId, "cancelled")}
                      data-testid={`crm360-tl-act-cancel-${ev.activityId}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-white/10 bg-white/[0.03] text-slate-400 text-[10.5px] hover:border-rose-400/40"><X className="w-3 h-3" /> Cancel</button>
                  </>
                ) : (
                  <button type="button" onClick={() => onActivityStatus(ev.activityId, "open")}
                    data-testid={`crm360-tl-act-reopen-${ev.activityId}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 text-[10.5px] hover:bg-amber-500/20"><RotateCcw className="w-3 h-3" /> Reopen</button>
                )}
              </div>
            )}
            {ev.type === "quote" && ev.quoteId && <QuoteNotesPeek quoteId={ev.quoteId} quoteRef={ev.quoteRef} notes={ev.quoteNotes} />}
            {bodyStr && (
              <div className="text-sm text-slate-300 whitespace-pre-wrap break-words leading-relaxed">
                {bodyStr.slice(0, 1200)}
                {bodyStr.length > 1200 && <span className="text-slate-500"> …</span>}
              </div>
            )}
            {ev.linkTo && (
              canDrill ? (
                <button
                  type="button"
                  onClick={() => onDrill(ev)}
                  className="inline-flex items-center gap-1 text-[11px] text-orange-400 hover:text-orange-300 font-mono"
                  data-testid={`crm360-timeline-drill-${ev.type}`}
                >
                  View <ChevronRight className="w-3 h-3" />
                </button>
              ) : ev.external ? (
                <a
                  href={ev.linkTo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-orange-400 hover:text-orange-300 font-mono"
                  data-testid={`crm360-timeline-link-${ev.type}`}
                >
                  Open <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <Link
                  to={ev.linkTo}
                  className="inline-flex items-center gap-1 text-[11px] text-orange-400 hover:text-orange-300 font-mono"
                >
                  View <ExternalLink className="w-3 h-3" />
                </Link>
              )
            )}
            {ev.type === "whatsapp" && ev.phoneKey && onViewWhatsApp && (
              <button
                type="button"
                onClick={() => onViewWhatsApp(ev)}
                className="inline-flex items-center gap-1 text-[11px] text-green-300 hover:text-green-200 font-mono"
                data-testid="crm360-wa-view-conversation"
              >
                View conversation <ChevronRight className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── UI: Company & Credit intelligence panel (Wave L) ─────────────────
// Rail section that shows editable company/social/credit fields for a
// person doc. Toggles into an inline form on Edit; PATCHes /crm/persons/{id}.
// AccountManager is a dropdown of Talmia users fetched from GET /api/users.
function CompanyCreditPanel({ person, personId, onSaved }) {
  const can = useCan("/crm/customers");
  const [editing, setEditing] = useState(false);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const GAP_FIELDS = ["company_number", "credit_check", "credit_limit", "website", "companies_house_url", "linkedin_url", "facebook", "instagram", "company_summary"];
  const gaps = GAP_FIELDS.filter((k) => !person[k]).length;
  const [form, setForm] = useState({
    company_summary:     person.company_summary     || "",
    website:             person.website             || "",
    company_number:      person.company_number      || "",
    credit_check:        person.credit_check        || "",
    credit_limit:        person.credit_limit        || "",
    companies_house_url: person.companies_house_url || "",
    linkedin_url:        person.linkedin_url        || "",
    facebook:            person.facebook            || "",
    instagram:           person.instagram           || "",
  });

  // Load user list once when entering edit mode (dropdown for AccountManager).
  useEffect(() => {
    if (!editing || users.length) return;
    let alive = true;
    axios.get(`${API}/users`, { withCredentials: true })
      .then((r) => { if (alive) setUsers(r.data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [editing, users.length]);

  // Re-seed form when the underlying person changes (after save reload).
  useEffect(() => {
    if (editing) return;
    setForm({
      company_summary:     person.company_summary     || "",
      website:             person.website             || "",
      company_number:      person.company_number      || "",
      credit_check:        person.credit_check        || "",
      credit_limit:        person.credit_limit        || "",
      companies_house_url: person.companies_house_url || "",
      linkedin_url:        person.linkedin_url        || "",
      facebook:            person.facebook            || "",
      instagram:           person.instagram           || "",
    });
  }, [person, editing]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/crm/persons/${personId}`, form, { withCredentials: true });
      toast.success("Company & credit info saved");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || "" });
    } finally { setSaving(false); }
  };

  return (
    <section
      className="rounded-lg border border-white/[0.06] bg-white/[0.02]"
      data-testid="crm360-rail-company-credit"
    >
      <div className="px-3 py-2 border-b border-white/[0.04] flex items-center justify-between gap-2">
        <span className="text-[10.5px] uppercase tracking-widest text-slate-400 font-mono flex items-center gap-1.5">
          <Briefcase className="w-3 h-3 text-amber-300" /> Company & credit
        </span>
        {editing ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="text-[10.5px] text-slate-400 hover:text-white font-mono px-1.5 py-0.5 rounded hover:bg-white/5"
              data-testid="cc-cancel"
              disabled={saving}
            ><X className="w-3 h-3" /></button>
            <button
              type="button"
              onClick={save}
              className="text-[10.5px] text-emerald-300 hover:text-emerald-200 font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 inline-flex items-center gap-1"
              data-testid="cc-save"
              disabled={saving}
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <span
              className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${gaps ? "bg-amber-500/15 text-amber-300 border-amber-500/30" : "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"}`}
              data-testid="cc-gap-badge"
              title={gaps ? `${gaps} enrichment field${gaps === 1 ? "" : "s"} still empty` : "All enrichment fields filled"}
            >
              {gaps ? `${gaps} gap${gaps === 1 ? "" : "s"}` : "complete"}
            </span>
            {can("edit") && <button
              type="button"
              disabled
              title="Auto-enrich from Companies House / web — coming soon"
              data-testid="cc-enrich"
              className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-white/10 bg-white/[0.03] text-slate-500 cursor-not-allowed inline-flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3" /> Enrich <span className="text-[8px] opacity-70">soon</span>
            </button>}
            {can("edit") && <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-[10.5px] text-slate-400 hover:text-white font-mono px-1.5 py-0.5 rounded hover:bg-white/5 inline-flex items-center gap-1"
              data-testid="cc-edit"
              title="Edit company & credit intelligence"
            ><Pencil className="w-3 h-3" /> Edit</button>}
          </div>
        )}
      </div>

      <div className="px-3 py-3 space-y-2">
        {editing ? (
          <CompanyCreditForm form={form} setForm={setForm} users={users} />
        ) : (
          <CompanyCreditReadout person={person} />
        )}
      </div>
    </section>
  );
}

function CompanyCreditReadout({ person }) {
  const rows = [
    { k: "credit_check",        label: "Credit check",    icon: ShieldCheck },
    { k: "credit_limit",        label: "Credit limit",    icon: CreditCard,  prefix: "£" },
    { k: "company_number",      label: "Company number",  icon: Landmark, mono: true },
    { k: "website",             label: "Website",         icon: Globe, url: true },
    { k: "companies_house_url", label: "Companies House", icon: Landmark, url: true },
    { k: "linkedin_url",        label: "LinkedIn",        icon: Linkedin, url: true },
    { k: "facebook",            label: "Facebook",        icon: FacebookIcon, url: true },
    { k: "instagram",           label: "Instagram",       icon: InstagramIcon, url: true },
  ];
  const anyValue = rows.some(r => person[r.k]) || person.company_summary;
  if (!anyValue) {
    return (
      <div className="text-[11px] text-slate-500 font-mono py-2 text-center" data-testid="cc-empty">
        No company or credit info yet — hit Edit to fill it in.
      </div>
    );
  }
  return (
    <div className="space-y-1.5" data-testid="cc-readout">
      {rows.map(({ k, label, icon: Icon, url, mono, prefix }) => {
        const v = person[k];
        if (!v) return null;
        const displayValue = prefix ? `${prefix}${v}` : v;
        return (
          <div key={k} className="flex items-baseline gap-2 text-[12px]" data-testid={`cc-row-${k}`}>
            <Icon className="w-3 h-3 text-slate-500 shrink-0 mt-0.5" />
            <span className="text-slate-500 font-mono text-[10px] uppercase tracking-widest w-24 shrink-0">{label}</span>
            {url ? (
              <a
                href={v.startsWith("http") ? v : `https://${v}`}
                target="_blank" rel="noopener noreferrer"
                className="text-sky-300 hover:text-sky-200 underline decoration-dotted underline-offset-2 truncate"
                title={v}
              >{v.replace(/^https?:\/\//, "").replace(/\/$/, "")}</a>
            ) : (
              <span className={`text-slate-200 truncate ${mono ? "font-mono text-[11.5px]" : ""}`} title={v}>
                {displayValue}
              </span>
            )}
          </div>
        );
      })}
      {person.company_summary && (
        <div className="pt-2 mt-2 border-t border-white/5">
          <div className="text-slate-500 font-mono text-[10px] uppercase tracking-widest mb-1">Company summary</div>
          <div className="text-[12px] text-slate-300 leading-relaxed whitespace-pre-wrap" data-testid="cc-row-company_summary">
            {person.company_summary}
          </div>
        </div>
      )}
    </div>
  );
}

function CompanyCreditForm({ form, setForm, users }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const input = "w-full px-2 py-1.5 rounded bg-slate-900/60 border border-white/10 text-slate-100 text-[12px] font-mono focus:outline-none focus:ring-1 focus:ring-orange-500/60 focus:border-orange-500/40 placeholder:text-slate-600";
  const label = "block text-[9.5px] uppercase tracking-widest text-slate-500 font-mono mb-0.5";
  return (
    <div className="space-y-2.5" data-testid="cc-form">
      <div>
        <label className={label}>Company summary</label>
        <textarea
          value={form.company_summary}
          onChange={(e) => set("company_summary", e.target.value)}
          rows={3}
          placeholder="Short internal note about the customer's business…"
          className={input}
          data-testid="cc-input-company_summary"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={label}>Company #</label>
          <input value={form.company_number} onChange={(e) => set("company_number", e.target.value)}
            placeholder="12345678" className={input} data-testid="cc-input-company_number" />
        </div>
        <div>
          <label className={label}>Credit check</label>
          <input value={form.credit_check} onChange={(e) => set("credit_check", e.target.value)}
            placeholder="Passed · 2026-01-14" className={input} data-testid="cc-input-credit_check" />
        </div>
        <div>
          <label className={label}>Credit limit (£)</label>
          <input value={form.credit_limit} onChange={(e) => set("credit_limit", e.target.value)}
            placeholder="10000" className={input} data-testid="cc-input-credit_limit" />
        </div>
        <div>
          <label className={label}>Website</label>
          <input type="url" value={form.website} onChange={(e) => set("website", e.target.value)}
            placeholder="https://…" className={input} data-testid="cc-input-website" />
        </div>
      </div>
      <div>
        <label className={label}>Companies House URL</label>
        <input type="url" value={form.companies_house_url}
          onChange={(e) => set("companies_house_url", e.target.value)}
          placeholder="https://find-and-update.company-information.service.gov.uk/company/…"
          className={input} data-testid="cc-input-companies_house_url" />
      </div>
      <div>
        <label className={label}>LinkedIn URL</label>
        <input type="url" value={form.linkedin_url}
          onChange={(e) => set("linkedin_url", e.target.value)}
          placeholder="https://linkedin.com/in/…"
          className={input} data-testid="cc-input-linkedin_url" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={label}>Facebook</label>
          <input type="url" value={form.facebook} onChange={(e) => set("facebook", e.target.value)}
            placeholder="https://facebook.com/…" className={input} data-testid="cc-input-facebook" />
        </div>
        <div>
          <label className={label}>Instagram</label>
          <input type="url" value={form.instagram} onChange={(e) => set("instagram", e.target.value)}
            placeholder="https://instagram.com/…" className={input} data-testid="cc-input-instagram" />
        </div>
      </div>
    </div>
  );
}

// ─── UI: right-rail cards ────────────────────────────────────────────────
// Collaborators avatar stack for the top-right panel — mirrors View Quotes'
// Owner/Collaborators layout. Source = touchpoints (everyone who's called,
// quoted or noted on this customer). Replaces the old "Who's touched this".
function CwCollaborators({ personId }) {
  const [actors, setActors] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/crm/persons/${personId}/touchpoints`, { withCredentials: true })
      .then((r) => { if (alive) setActors(r.data?.actors || []); })
      .catch(() => { if (alive) setActors([]); });
    return () => { alive = false; };
  }, [personId]);
  if (actors === null) return <span className="text-slate-600 text-[11px]">…</span>;
  if (!actors.length) return <span className="text-slate-500 text-[11px] font-mono">No collaborators yet</span>;
  const shown = actors.slice(0, 4);
  const extra = actors.length - shown.length;
  const initials = (name) => (name || "?").split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const colors = ["bg-sky-500/25 text-sky-200", "bg-purple-500/25 text-purple-200", "bg-emerald-500/25 text-emerald-200", "bg-amber-500/25 text-amber-200"];
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="crm360-collaborators-stack">
      <span className="flex -space-x-1.5">
        {shown.map((a, i) => (
          <span key={a.actor} title={`${a.actor} · ${a.total} touches`}
            className={`w-6 h-6 rounded-full grid place-items-center text-[9px] font-bold ring-2 ring-[#0b0f17] ${colors[i % colors.length]}`}>
            {initials(a.actor)}
          </span>
        ))}
      </span>
      {extra > 0 && <span className="text-[10px] text-slate-400 font-mono">+{extra}</span>}
    </span>
  );
}


function RailSection({ title, count, testId, children, defaultOpen = false, highlight = false, order, peek = null, openSignal = null }) {
  const [open, setOpen] = useState(defaultOpen);
  // Expand-all / Collapse-all from the shelf toolbar drives this.
  useEffect(() => {
    if (openSignal && typeof openSignal.value === "boolean") setOpen(openSignal.value);
  }, [openSignal]);
  return (
    <section
      style={order != null ? { order } : undefined}
      className={`rounded-lg border bg-white/[0.02] ${highlight ? "border-orange-500/30" : "border-white/[0.06]"}`}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-white/[0.02] gap-3"
        data-testid={testId ? `${testId}-toggle` : undefined}
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 min-w-0">
          <span className={`text-[10.5px] uppercase tracking-widest font-mono shrink-0 ${highlight ? "text-orange-300" : "text-slate-400"}`}>{title}</span>
          {!open && peek && (
            <span className="text-[11px] text-slate-500 truncate font-mono">{peek}</span>
          )}
        </span>
        <span className="flex items-center gap-2 shrink-0">
          <span className="text-[10.5px] text-slate-500 font-mono">{count}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${open ? "" : "-rotate-90"}`} />
        </span>
      </button>
      {open && <div className="border-t border-white/[0.04] divide-y divide-white/[0.04]">{children}</div>}
    </section>
  );
}

/** Individual quote row inside the CRM person right rail — open + archive inline
    so sales can prune stale quotes from where they live (the customer 360). */
function QuoteRailRow({ q, onArchived }) {
  const [busy, setBusy] = useState(false);
  const isArchived = q.status === "Archived";
  const doArchive = async (e) => {
    e.stopPropagation(); e.preventDefault();
    if (!isArchived && !window.confirm(`Archive quote ${q.quote_ref || ""}? It will be hidden from active lists (restore any time from Old quotes).`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/quotes/${q.id}/${isArchived ? "unarchive" : "archive"}`, isArchived ? {} : { reason: "Archived from Customer 360" }, { withCredentials: true });
      toast.success(isArchived ? "Quote restored" : "Quote archived");
      onArchived?.();
    } catch { toast.error("Action failed"); }
    finally { setBusy(false); }
  };
  return (
    <div
      className={`group px-3 py-2 hover:bg-white/[0.04] transition-colors border-l-2 border-transparent hover:border-orange-500/60 ${isArchived ? "opacity-60" : ""}`}
      data-testid={`crm360-quote-row-${q.id}`}
    >
      <div className="flex items-center gap-2">
        <Link
          to={`/quotes/${q.id}/edit`}
          className="flex-1 min-w-0 block"
          data-testid={`crm360-quote-link-${q.id}`}
        >
          <div className="flex justify-between items-baseline gap-2">
            <span className="text-sm text-white font-medium truncate">{q.quote_ref}</span>
            {q.total != null && <span className="text-sm text-orange-300 tabular-nums shrink-0">{money(q.total)}</span>}
          </div>
          {q.project && <div className="text-[11.5px] text-amber-200/90 font-medium truncate" title={q.project} data-testid={`crm360-quote-project-${q.id}`}>{q.project}</div>}
          <div className="text-[11px] text-slate-500 font-mono truncate">
            {fmtDay(q.created_date)} · {q.status || "?"}{q.account_manager && ` · ${q.account_manager}`}
          </div>
        </Link>
        <button
          onClick={doArchive}
          disabled={busy}
          className={`px-2 py-1 rounded text-[10px] font-mono uppercase tracking-widest border transition ${
            isArchived
              ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
              : "border-teal-500/40 text-teal-300 hover:bg-teal-500/10"
          } opacity-0 group-hover:opacity-100 disabled:opacity-40`}
          data-testid={`crm360-quote-${isArchived ? "restore" : "archive"}-${q.id}`}
          title={isArchived ? "Restore this quote" : "Archive this quote"}
        >
          {isArchived ? "Restore" : "Archive"}
        </button>
      </div>
    </div>
  );
}

// ─── UI: Contact & channels card (v0.7 §7D.4) ────────────────────────────
// CRM-grade contact record: full address + the new channel fields
// (mobile, WhatsApp, website, socials, VAT, 2nd email), editable in place
// via the extended PATCH /crm/persons/{id} allow-list.
const CONTACT_ROWS = [
  { k: "mobile",          label: "Mobile",       icon: Smartphone },
  { k: "whatsapp",        label: "WhatsApp",     icon: MessageCircle },
  { k: "secondary_email", label: "2nd email",    icon: MailIcon },
  { k: "website",         label: "Website",      icon: Globe, url: true },
  { k: "instagram",       label: "Instagram",    icon: InstagramIcon, url: true },
  { k: "linkedin_url",    label: "LinkedIn",     icon: Linkedin, url: true },
  { k: "facebook",        label: "Facebook",     icon: FacebookIcon, url: true },
  { k: "vat_number",      label: "VAT no.",      icon: Landmark, mono: true },
  { k: "title",           label: "Job title",    icon: Briefcase },
  { k: "preferred_channel", label: "Preferred",  icon: MessageSquare },
];
const CONTACT_FIELDS = [
  "title", "mobile", "whatsapp", "secondary_email", "website", "instagram",
  "linkedin_url", "facebook", "vat_number", "preferred_channel",
];



function ContactChannelsCard({ person, personId, onSaved, openSignal }) {
  const can = useCan("/crm/customers");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const seed = () => Object.fromEntries(CONTACT_FIELDS.map((k) => [k, person[k] || ""]));
  const [form, setForm] = useState(seed);
  // Contact-only gap fields — the 4 social/web fields (website, instagram,
  // linkedin_url, facebook) are owned by Company & Credit for gap tracking, so
  // a single missing field is never double-flagged across two badges.
  const CONTACT_GAP_FIELDS = ["title", "mobile", "whatsapp", "secondary_email", "vat_number", "preferred_channel"];
  const gaps = CONTACT_GAP_FIELDS.filter((k) => !person[k]).length;
  useEffect(() => { if (!editing) setForm(seed()); /* eslint-disable-next-line */ }, [person]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/crm/persons/${personId}`, form, { withCredentials: true });
      toast.success("Contact details saved");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || "" });
    } finally { setSaving(false); }
  };

  const peek = person.phone || person.email || "No contact channels yet";
  const input = "w-full px-2 py-1.5 rounded bg-slate-900/60 border border-white/10 text-slate-100 text-[12px] font-mono focus:outline-none focus:ring-1 focus:ring-orange-500/60 placeholder:text-slate-600";
  const label = "block text-[9.5px] uppercase tracking-widest text-slate-500 font-mono mb-0.5";

  return (
    <RailSection title="Contact & channels" testId="crm360-rail-contact" peek={peek} openSignal={openSignal} highlight>
      <div className="px-3 py-3 space-y-2">
        <div className="flex items-center justify-end">
          {editing ? (
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setEditing(false)} disabled={saving}
                className="text-[10.5px] text-slate-400 hover:text-white font-mono px-1.5 py-0.5 rounded hover:bg-white/5" data-testid="contact-cancel"><X className="w-3 h-3" /></button>
              <button type="button" onClick={save} disabled={saving} data-testid="contact-save"
                className="text-[10.5px] text-emerald-300 hover:text-emerald-200 font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 inline-flex items-center gap-1">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <span
                className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${gaps ? "bg-amber-500/15 text-amber-300 border-amber-500/30" : "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"}`}
                data-testid="contact-gap-badge"
                title={gaps ? `${gaps} contact field${gaps === 1 ? "" : "s"} still empty` : "All contact fields filled"}
              >
                {gaps ? `${gaps} gap${gaps === 1 ? "" : "s"}` : "complete"}
              </span>
              {can("edit") && <button type="button" onClick={() => setEditing(true)} data-testid="contact-edit"
                className="text-[10.5px] text-slate-400 hover:text-white font-mono px-1.5 py-0.5 rounded hover:bg-white/5 inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>}
            </div>
          )}
        </div>

        {editing ? (
          <div className="space-y-2.5" data-testid="contact-form">
            <div className="grid grid-cols-2 gap-2">
              <div><label className={label}>Job title</label><input value={form.title} onChange={(e) => set("title", e.target.value)} className={input} data-testid="contact-input-title" /></div>
              <div><label className={label}>Mobile</label><input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className={input} placeholder="07…" data-testid="contact-input-mobile" /></div>
              <div><label className={label}>WhatsApp</label><input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className={input} placeholder="+44…" data-testid="contact-input-whatsapp" /></div>
              <div><label className={label}>2nd email</label><input value={form.secondary_email} onChange={(e) => set("secondary_email", e.target.value)} className={input} data-testid="contact-input-secondary_email" /></div>
              <div><label className={label}>Website</label><input value={form.website} onChange={(e) => set("website", e.target.value)} className={input} placeholder="https://…" data-testid="contact-input-website" /></div>
              <div><label className={label}>Instagram</label><input value={form.instagram} onChange={(e) => set("instagram", e.target.value)} className={input} data-testid="contact-input-instagram" /></div>
              <div><label className={label}>LinkedIn</label><input value={form.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} className={input} data-testid="contact-input-linkedin_url" /></div>
              <div><label className={label}>Facebook</label><input value={form.facebook} onChange={(e) => set("facebook", e.target.value)} className={input} data-testid="contact-input-facebook" /></div>
              <div><label className={label}>VAT no.</label><input value={form.vat_number} onChange={(e) => set("vat_number", e.target.value)} className={input} data-testid="contact-input-vat_number" /></div>
              <div><label className={label}>Preferred channel</label><input value={form.preferred_channel} onChange={(e) => set("preferred_channel", e.target.value)} className={input} placeholder="WhatsApp / Email / Phone" data-testid="contact-input-preferred_channel" /></div>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="contact-readout">
            {person.phone && <ContactRow icon={Phone} label="Phone" value={person.phone} />}
            {person.email && <ContactRow icon={Mail} label="Email" value={person.email} />}
            {CONTACT_ROWS.map(({ k, label: l, icon, url, mono }) => person[k] ? (
              <ContactRow key={k} icon={icon} label={l} value={person[k]} url={url} mono={mono} testid={`contact-row-${k}`} />
            ) : null)}
            {!person.phone && !person.email && CONTACT_ROWS.every((r) => !person[r.k]) && (
              <div className="text-[11px] text-slate-500 font-mono py-2 text-center" data-testid="contact-empty">No contact channels yet — hit Edit to add them.</div>
            )}
          </div>
        )}
      </div>
    </RailSection>
  );
}

function ContactRow({ icon: Icon, label, value, url, mono, testid }) {
  return (
    <div className="flex items-baseline gap-2 text-[12px]" data-testid={testid}>
      <Icon className="w-3 h-3 text-slate-500 shrink-0 mt-0.5" />
      <span className="text-slate-500 font-mono text-[10px] uppercase tracking-widest w-20 shrink-0">{label}</span>
      {url ? (
        <a href={String(value).startsWith("http") ? value : `https://${value}`} target="_blank" rel="noopener noreferrer"
          className="text-sky-300 hover:text-sky-200 underline decoration-dotted underline-offset-2 truncate" title={value}>
          {String(value).replace(/^https?:\/\//, "").replace(/\/$/, "")}
        </a>
      ) : (
        <span className={`text-slate-200 truncate ${mono ? "font-mono text-[11.5px]" : ""}`} title={value}>{value}</span>
      )}
    </div>
  );
}

// ─── UI: Full data record (v0.7 §7C) — admin/management only ──────────────
const RECORD_GROUPS = [
  { title: "Identity",   keys: ["_id", "PersonId", "FullName", "FirstName", "LastName", "Title", "Names"] },
  { title: "Contact",    keys: ["Email", "SecondaryEmail", "Phone", "Mobile", "WhatsApp", "WhatsAppOptIn", "PreferredChannel", "Emails", "Phones"] },
  { title: "Address",    keys: ["Addresses", "City", "State", "PostCode", "Country", "Data8Errors", "SiteAddresses"] },
  { title: "Company & credit", keys: ["Company", "CompanyName", "Companies", "CompanyNumber", "VatNumber", "Website", "CreditCheck", "CreditLimit", "CompaniesHouseURL", "CompanySummary", "Instagram", "LinkedinURL", "Facebook"] },
  { title: "Commercial", keys: ["ShortVIPLevel", "LongVIPLevel", "TotalOrdersValue", "AverageOrderValue", "NumberOfOrders", "NumberOfRegOrders", "NumberOfGuestOrders", "LastOrderDate", "OrderIds", "BCCustIds", "BCCustGroupId", "Tags"] },
  { title: "Pipeline & ownership", keys: ["PipelineId", "StageId", "PrimaryAM", "AccountManager", "TierOverride"] },
  { title: "AI enrichment", keys: ["_ai_person"] },
  { title: "Meta",       keys: ["CreatedDate", "AddTime", "UpdatedDate", "UpdatedBy", "TierHistory", "AMHistory"] },
];

function FullDataRecord({ raw, aiEnrichment, canView }) {
  const [hideEmpty, setHideEmpty] = useState(true);
  const [showRaw, setShowRaw] = useState(false);
  if (!canView || !raw) return null;

  const isEmpty = (v) => v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0) || (typeof v === "object" && !Array.isArray(v) && v && Object.keys(v).length === 0);
  const fmtVal = (v) => {
    if (isEmpty(v)) return "—";
    if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? "" : "s"}`;
    if (typeof v === "object") return JSON.stringify(v);
    if (typeof v === "boolean") return v ? "true" : "false";
    return String(v);
  };

  return (
    <section className="rounded-lg border border-white/[0.06] bg-white/[0.02]" data-testid="crm360-full-record">
      <details>
        <summary className="px-4 py-3 cursor-pointer flex items-center justify-between hover:bg-white/[0.02] list-none">
          <span className="flex items-center gap-2">
            <Database className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-[11px] uppercase tracking-widest font-mono text-slate-300">Full data record</span>
            <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/30">admin</span>
          </span>
          <ChevronDown className="w-4 h-4 text-slate-500" />
        </summary>
        <div className="px-4 py-4 border-t border-white/[0.06] space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" onClick={() => setHideEmpty((v) => !v)} data-testid="record-hide-empty"
              className={`text-[10.5px] font-mono px-2 py-1 rounded-md border ${hideEmpty ? "bg-orange-500/15 text-orange-300 border-orange-500/40" : "border-white/10 text-slate-400"}`}>
              {hideEmpty ? "Hiding empty fields" : "Showing empty fields"}
            </button>
            <button type="button" onClick={() => setShowRaw((v) => !v)} data-testid="record-toggle-raw"
              className={`text-[10.5px] font-mono px-2 py-1 rounded-md border ${showRaw ? "bg-white/10 text-white border-white/25" : "border-white/10 text-slate-400"}`}>
              {showRaw ? "Grouped view" : "Show raw JSON"}
            </button>
          </div>

          {showRaw ? (
            <pre className="text-[11px] font-mono text-slate-300 bg-black/40 rounded-lg p-3 overflow-x-auto max-h-[420px]" data-testid="record-raw-json">
              {JSON.stringify(raw, null, 2)}
            </pre>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {RECORD_GROUPS.map((g) => {
                const rows = g.keys.map((k) => [k, k === "_ai_person" ? (aiEnrichment?.person_block ?? raw[k]) : raw[k]])
                  .filter(([, v]) => !hideEmpty || !isEmpty(v));
                if (rows.length === 0) return null;
                return (
                  <div key={g.title} className="rounded-lg border border-white/[0.05] p-3">
                    <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono mb-2">{g.title}</div>
                    <div className="space-y-1">
                      {rows.map(([k, v]) => (
                        <div key={k} className={`flex items-baseline gap-2 text-[11.5px] ${isEmpty(v) ? "opacity-40" : ""}`}>
                          <span className="text-slate-500 font-mono text-[10px] w-40 shrink-0 truncate">{k}</span>
                          <span className="text-slate-200 font-mono break-all">{fmtVal(v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </details>
    </section>
  );
}

// ─── UI: Drill-in modal (v0.7 §5 return contract) ─────────────────────────
// A clean fixed modal (portalled to <body>) layered ABOVE the 360. The 360
// stays mounted + unscrolled underneath, so ← Back returns to the exact spot.
function DrillInDrawer({ ev, onBack, returnState }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onBack(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onBack]);
  if (!ev) return null;
  const meta = TYPE_META[ev.type] || TYPE_META.note;
  const Icon = meta.icon;
  const TYPE_LABEL = { order: "Order", quote: "Quote", call: "Call", note: "Note", activity: "Activity", attachment: "File" };

  return createPortal(
    <div
      className="fixed inset-0 z-[140] flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onBack(); }}
      data-testid="crm360-drillin"
    >
      <div className="w-full max-w-2xl my-8 rounded-2xl border border-white/10 bg-[#0E1524] shadow-2xl shadow-black/60 overflow-hidden">
        <div className="bg-[#0B111C] border-b border-white/10 px-4 py-3 flex items-center justify-between">
          <button
            type="button"
            onClick={onBack}
            data-testid="crm360-drillin-back"
            className="inline-flex items-center gap-1.5 text-[12px] font-mono uppercase tracking-widest text-slate-300 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" /> Back to timeline
          </button>
          <div className="flex items-center gap-3">
            {ev.linkTo && !ev.external && (
              <Link
                to={ev.linkTo}
                state={returnState}
                data-testid="crm360-drillin-openfull"
                className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-orange-300 hover:text-orange-200"
              >
                Open full <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            )}
            <button type="button" onClick={onBack} className="text-slate-400 hover:text-white" aria-label="Close" data-testid="crm360-drillin-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ${meta.bg} ring-1 ${meta.ring} grid place-items-center shrink-0`}>
              <Icon className={`w-5 h-5 ${meta.iconColor}`} />
            </div>
            <div className="min-w-0">
              <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono">{TYPE_LABEL[ev.type] || "Event"}</div>
              <div className="text-lg font-display font-semibold text-white truncate">{ev.title}</div>
              <div className="text-[11px] text-slate-500 font-mono">{fmtDate(ev.timestamp)} · {relTime(ev.timestamp)}</div>
            </div>
          </div>
          {ev.meta?.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {ev.meta.map((m, i) => (
                <div key={`dm-${i}`} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-[12px] text-slate-200 font-mono">{m}</div>
              ))}
            </div>
          )}
          {ev.body && (
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">
              {textOnly(ev.body)}
            </div>
          )}
          <div className="text-[11px] text-slate-500 font-mono pt-1 border-t border-white/[0.05]">
            Peeked without leaving this contact. <span className="text-slate-300">Back</span> returns to the timeline exactly where you were · <span className="text-orange-300">Open full</span> loads the complete record.
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── UI: WhatsApp conversation thread drawer (contact-level) ──────────────
function WhatsAppThreadDrawer({ phoneKey, highlightId, contactName, onBack }) {
  const [msgs, setMsgs] = useState(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onBack(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onBack]);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/whatsapp/contacts/${encodeURIComponent(phoneKey)}/messages`, { withCredentials: true })
      .then((r) => { if (alive) setMsgs(r.data?.messages || []); })
      .catch(() => { if (alive) setMsgs([]); });
    return () => { alive = false; };
  }, [phoneKey]);

  return createPortal(
    <div
      className="fixed inset-0 z-[150] flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onBack(); }}
      data-testid="crm360-wa-thread"
    >
      <div className="w-full max-w-2xl my-8 rounded-2xl border border-green-500/20 bg-[#0E1524] shadow-2xl shadow-black/60 overflow-hidden">
        <div className="bg-[#0B111C] border-b border-white/10 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <MessageCircle className="w-4 h-4 text-green-300 shrink-0" />
            <div className="min-w-0">
              <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono">WhatsApp conversation</div>
              <div className="text-sm font-display font-semibold text-white truncate">{contactName || "Contact"}</div>
            </div>
          </div>
          <button type="button" onClick={onBack} className="text-slate-400 hover:text-white" aria-label="Close" data-testid="crm360-wa-thread-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-2 max-h-[72vh] overflow-y-auto">
          {msgs === null && <div className="py-6 text-center text-sm text-slate-500">Loading conversation…</div>}
          {msgs && msgs.length === 0 && <div className="py-6 text-center text-sm text-slate-500">No messages.</div>}
          {msgs && msgs.map((m) => {
            const out = m.direction === "outbound";
            const hot = highlightId && m.message_id === highlightId;
            return (
              <div
                key={m.message_id}
                ref={hot ? (el) => el && el.scrollIntoView({ block: "center" }) : undefined}
                className={`flex ${out ? "justify-end" : "justify-start"}`}
              >
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 border ${out
                  ? "bg-orange-500/10 border-orange-500/30 rounded-br-sm"
                  : "bg-teal-500/10 border-teal-500/30 rounded-bl-sm"} ${hot ? "ring-2 ring-green-400/70" : ""}`}
                  data-testid={hot ? "crm360-wa-thread-highlight" : undefined}
                >
                  <div className={`flex items-center gap-1.5 mb-0.5 text-[10px] font-mono uppercase tracking-widest ${out ? "text-orange-300" : "text-teal-300"}`}>
                    {out ? "↗ Sent" : "↙ Received"}
                    <span className="text-slate-500 normal-case tracking-normal">· {fmtDate(m.sent_at)}</span>
                  </div>
                  <div className={`text-sm whitespace-pre-wrap break-words ${out ? "text-orange-200" : "text-teal-200"}`}>{m.body}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── UI: Deal-stage strip (P4) — named crm/stages, current one highlighted ──
const STAGE_COLOR = {
  slate:   "bg-slate-500/15 text-slate-200 border-slate-400/40",
  sky:     "bg-sky-500/15 text-sky-200 border-sky-400/40",
  fuchsia: "bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-400/40",
  emerald: "bg-emerald-500/15 text-emerald-200 border-emerald-400/40",
  amber:   "bg-amber-500/15 text-amber-200 border-amber-400/40",
  orange:  "bg-orange-500/15 text-orange-200 border-orange-400/40",
  teal:    "bg-teal-500/15 text-teal-200 border-teal-400/40",
  rose:    "bg-rose-500/15 text-rose-200 border-rose-400/40",
  violet:  "bg-violet-500/15 text-violet-200 border-violet-400/40",
  blue:    "bg-blue-500/15 text-blue-200 border-blue-400/40",
  cyan:    "bg-cyan-500/15 text-cyan-200 border-cyan-400/40",
};

function DealStageStrip({ currentStageKey }) {
  const [stages, setStages] = useState([]);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/crm/stages`, { withCredentials: true })
      .then((r) => { if (alive) setStages(r.data?.items || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!stages.length) return null;
  const curIdx = stages.findIndex((s) => s.key === currentStageKey);
  return (
    <section className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3" data-testid="crm360-stage-strip">
      <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-mono mb-2 flex items-center gap-1.5">
        <TrendingUp className="w-3 h-3 text-teal-300" /> Deal stage
        {curIdx < 0 && <span className="text-slate-600 normal-case tracking-normal">· not on a Talmia stage yet</span>}
      </div>
      <div className="flex items-stretch gap-1 overflow-x-auto pb-1" data-testid="crm360-stage-strip-track">
        {stages.map((s, i) => {
          const active = s.key === currentStageKey;
          const done = curIdx >= 0 && i < curIdx;
          const tone = STAGE_COLOR[s.color] || STAGE_COLOR.slate;
          return (
            <div key={s.key} data-testid={`crm360-stage-${s.key}`}
              className={`shrink-0 px-2.5 py-1.5 rounded-md border text-[10.5px] font-medium whitespace-nowrap transition-all ${
                active ? `${tone} ring-1 ring-white/25 brightness-125`
                : done ? "bg-white/[0.04] text-slate-400 border-white/10"
                : "bg-transparent text-slate-500 border-white/[0.06]"}`}
              title={s.ai_role || s.label}>
              <span className="opacity-60 font-mono mr-1">{i + 1}</span>{s.label}
              {active && <span className="ml-1.5 text-[8px] uppercase tracking-widest opacity-80">· now</span>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─── UI: AI Summary box (D08 — live person-level AI enrichment) ──────────────
function SENT_TONE(v) {
  return v === "positive" ? "text-emerald-300 bg-emerald-500/15 border-emerald-500/30"
    : v === "negative" ? "text-rose-300 bg-rose-500/15 border-rose-500/30"
    : v === "neutral" ? "text-sky-300 bg-sky-500/15 border-sky-500/30"
    : "text-slate-400 bg-white/[0.04] border-white/10";
}

function AiSummaryBox({ personId }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadedCache, setLoadedCache] = useState(false);

  // Load any cached summary on mount (silent 404 when none yet).
  useEffect(() => {
    if (!personId) return;
    let cancelled = false;
    axios.get(`${API}/crm/persons/${personId}/ai-summary`, { withCredentials: true })
      .then((r) => { if (!cancelled) setResult(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadedCache(true); });
    return () => { cancelled = true; };
  }, [personId]);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/crm/persons/${personId}/ai-summary`, {}, { withCredentials: true });
      setResult(data);
      toast.success("AI summary generated");
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "Could not generate AI summary"));
    } finally { setLoading(false); }
  };

  const s = result?.summary || null;
  const hasResult = s && (s.summary || (s.key_signals || []).length || s.next_best_action);

  return (
    <section className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3" data-testid="crm360-ai-box">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-mono flex items-center gap-1.5">
          <Sparkles className="w-3 h-3 text-violet-300" /> AI Summary
          <span className="text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/30" data-testid="crm360-ai-advisory-badge">
            Advisory only
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={generate} disabled={loading} data-testid="crm360-ai-generate"
            title={hasResult ? "Regenerate AI summary" : "Generate AI summary"}
            className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border border-violet-500/40 bg-violet-500/[0.08] text-violet-200 hover:bg-violet-500/[0.16] disabled:opacity-50 inline-flex items-center gap-1">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {loading ? "Generating" : hasResult ? "Regenerate" : "Generate"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="rounded-lg border border-dashed border-white/10 bg-white/[0.015] px-3 py-4 text-[12px] text-slate-500 leading-relaxed inline-flex items-center gap-2" data-testid="crm360-ai-loading">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Analysing recent calls, quotes and orders…
        </div>
      ) : hasResult ? (
        <div className="space-y-2.5" data-testid="crm360-ai-result">
          <p className="text-[13px] text-slate-200 leading-relaxed" data-testid="crm360-ai-summary-text">{s.summary || "—"}</p>

          <div className="flex flex-wrap items-center gap-1.5">
            {s.sentiment_trend && (
              <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${SENT_TONE(s.sentiment_trend)}`} data-testid="crm360-ai-sentiment">
                {s.sentiment_trend}
              </span>
            )}
            {(s.key_signals || []).map((sig, i) => (
              <span key={i} className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-white/10 bg-white/[0.04] text-slate-300" data-testid={`crm360-ai-signal-${i}`}>
                {sig}
              </span>
            ))}
          </div>

          {s.next_best_action && (
            <div className="rounded-lg border border-violet-500/20 bg-violet-500/[0.05] px-3 py-2" data-testid="crm360-ai-nba">
              <div className="text-[9px] uppercase tracking-widest text-violet-300/80 font-mono mb-0.5">Next best action</div>
              <div className="text-[12px] text-slate-200 leading-relaxed">{s.next_best_action}</div>
            </div>
          )}

          <div className="text-[10px] text-slate-500 leading-relaxed pt-1 border-t border-white/[0.06]" data-testid="crm360-ai-disclaimer">
            AI-generated — advisory only. A suggestion based on recent data, not a verified fact or instruction.
            {result?.generated_at && <> · {new Date(result.generated_at).toLocaleString("en-GB")}</>}
            {result?.generated_by && <> · {result.generated_by}</>}
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-white/10 bg-white/[0.015] px-3 py-4 text-[12px] text-slate-500 leading-relaxed" data-testid="crm360-ai-placeholder">
          A one-glance AI brief — recent sentiment, key signals and the next best action — from this customer's recent calls, quotes and orders. Click Generate to create one.
        </div>
      )}
    </section>
  );
}

// ─── main page ────────────────────────────────────────────────────────────
// Stable wrapper (module-level) so a theme switch never remounts the page or an open pop-up.
function Wrap({ overlay, lightOn, children }) {
  if (overlay) {
    return (
      <div data-talmia-theme={lightOn ? "light" : undefined}
        style={lightOn ? { background: "#ECF0F6", minHeight: "100%" } : { display: "contents" }}>{children}</div>
    );
  }
  return <Layout mainProps={lightOn ? { "data-talmia-theme": "light" } : {}}>{children}</Layout>;
}


export default function CRMPerson360({ personIdProp = null, mode = "page", onClose = null }) {
  const can = useCan("/crm/customers");
  const { id: routeId } = useParams();
  const id = personIdProp || routeId;
  const addrBook = useAddressBook(id);
  const navigate = useNavigate();
  const location = useLocation();
  const overlay = mode === "overlay";
  const { theme } = useTheme();
  const lightOn = theme === "light";
  const wrapProps = { overlay, lightOn };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);  // "activity" | "note" | null
  const [editAct, setEditAct] = useState(null); // CRM activity being edited
  const [showDoneWork, setShowDoneWork] = useState(false); // reveal done/cancelled in Open work
  const [tlFilter, setTlFilter] = useState("all"); // timeline channel filter
  const [wsTasks, setWsTasks] = useState([]);   // Workspace tasks linked to this person
  const [mediaCount, setMediaCount] = useState(null); // object-storage attachments for this person
  const [drill, setDrill] = useState(null);     // drill-in event (return contract §5)
  const [waThread, setWaThread] = useState(null); // { phoneKey, highlightId, contactName }
  const [emailThread, setEmailThread] = useState(null); // { highlightId } — opens the email drawer
  const [orderDrill, setOrderDrill] = useState(null); // order-id → reusable OrderDetailModal (05a §2)
  const [peekQuoteId, setPeekQuoteId] = useState(null); // quote-id → central QuotePeekModal (Pass 2)
  const [tlEventSignal, setTlEventSignal] = useState(null); // expand/collapse-all timeline events
  // Inline header edit — name/company/email/phone, like a normal CRM record.
  const [editHdr, setEditHdr] = useState(false);
  const [hdrForm, setHdrForm] = useState(null);
  const [savingHdr, setSavingHdr] = useState(false);
  const openHdrEdit = () => {
    setHdrForm({
      first_name: data?.person?.first_name || "",
      last_name:  data?.person?.last_name || "",
      company:    data?.person?.company || "",
      email:      data?.person?.email || "",
      phone:      data?.person?.phone || "",
    });
    setEditHdr(true);
  };
  const saveHdr = async () => {
    setSavingHdr(true);
    try {
      await axios.patch(`${API}/crm/persons/${id}`, hdrForm, { withCredentials: true });
      await load();
      setEditHdr(false);
      toast.success("Details updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSavingHdr(false); }
  };
  const timelineRef = useRef(null);

  // returnTo token carried from the launching surface (deals/leads/etc.).
  const returnTo = location.state?.returnTo || null;

  // "Open full profile ↗" from the overlay — carry a returnTo token so the
  // full page's Back returns to the exact launching surface + reopens the 360.
  const openFullProfile = () => {
    const state = { returnTo: { route: location.pathname, scrollY: window.scrollY, open360PersonId: id } };
    navigate(`/crm/persons/${id}`, { state });
    onClose?.();
  };

  // Page-mode Back — honour the returnTo token; reopen the 360 on the source.
  const goBack = () => {
    if (returnTo?.route) {
      navigate(returnTo.route, { state: returnTo.open360PersonId ? { open360: returnTo.open360PersonId } : undefined });
    } else {
      navigate("/crm/customers");
    }
  };

  const load = () => {
    setLoading(true);
    setError(null);
    return axios
      .get(`${API}/crm/persons/${id}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  };
  const loadWorkspaceTasks = () => {
    axios.get(`${API}/workspace/persons/${id}/tasks`, { withCredentials: true })
      .then((r) => setWsTasks(r.data.items || []))
      .catch(() => setWsTasks([]));
  };
  // Attachments-card count = object-storage media for this person (same source
  // the expanded MediaManager shows), so composer uploads tick it up live.
  const loadMediaCount = () => {
    axios.get(`${API}/media`, { params: { context_type: "crm", entity_id: id, limit: 1 }, withCredentials: true })
      .then((r) => setMediaCount(r.data?.total ?? 0))
      .catch(() => setMediaCount(null));
  };
  useEffect(() => { load(); loadWorkspaceTasks(); loadMediaCount(); }, [id]);

  // PD SYNC — clear this person's stage override so their tier/stage
  // re-derives from Pipedrive-mirrored PipelineId + StageId + ShortVIPLevel.
  const [syncing, setSyncing] = useState(false);
  const runPdSync = async () => {
    setSyncing(true);
    try {
      const { data: r } = await axios.post(`${API}/pipedrive/sync/${id}`, {}, { withCredentials: true });
      toast.success("PD SYNC done", {
        description: r.derived?.tier
          ? `Tier: ${r.derived.tier} · Stage: ${r.derived.stage_key || "—"}`
          : "Stage/tier re-derived from Mongo",
      });
      await load();
    } catch (e) {
      toast.error("PD SYNC failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSyncing(false);
    }
  };

  const timeline = useMemo(() => {
    if (!data) return [];
    return buildTimeline(
      data.person, data.orders, data.calls, data.quotes,
      data.talmia_activities, data.talmia_notes, data.emails,
    );
  }, [data]);
  const tlCounts = useMemo(() => {
    const c = {};
    for (const ev of timeline) {
      const ch = CHANNEL_OF[ev.type] || "other";
      c[ch] = (c[ch] || 0) + 1;
    }
    return c;
  }, [timeline]);
  const filteredTimeline = tlFilter === "all"
    ? timeline
    : timeline.filter((e) => (CHANNEL_OF[e.type] || "other") === tlFilter);

  if (loading) return <Wrap {...wrapProps}><div className="p-8 text-slate-400" data-testid="crm360-loading">Loading customer…</div></Wrap>;
  if (error)   return <Wrap {...wrapProps}><div className="p-8 text-rose-300" data-testid="crm360-error">Error: {error}</div></Wrap>;
  if (!data || !data.person) return (
    <Wrap {...wrapProps}>
      <div className="p-8 text-center" data-testid="crm360-empty">
        <div className="text-slate-300 text-sm mb-1">Customer record not found</div>
        <div className="text-[12px] text-slate-500 mb-5 max-w-md mx-auto">
          This contact may have been merged, deleted, or is a lead without a full CRM profile yet.
        </div>
        {overlay && onClose && (
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-white/15 text-slate-200 hover:bg-white/5 text-sm" data-testid="crm360-empty-close">
            Close
          </button>
        )}
      </div>
    </Wrap>
  );

  const { person, orders, calls, quotes, related_contacts, ai_enrichment, raw_record, can_view_raw } = data;
  const initials = (person.full_name || "?")
    .split(/\s+/).map((n) => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const primaryCompany = person.company || (person.companies?.[0]) || null;
  const address = addressOneLine(addrBook.data?.billing) || [person.address1, person.address2, person.city, person.state, person.post_code, person.country]
    .filter(Boolean).join(", ");

  // Lifecycle: a lead is a customer who hasn't paid yet; converts on first order.
  const isCustomer = (person.orders_count || 0) > 0 || (person.ltv || 0) > 0;
  const openQuoteValue = (quotes || [])
    .filter((q) => !["won", "lost", "archived", "accepted", "rejected", "expired", "paid"].includes(String(q.status || "").toLowerCase()))
    .reduce((s, q) => s + (Number(q.total) || 0), 0);

  // returnTo token for drill-in "Open full" links (back to this 360).
  const drillReturnState = { returnTo: { route: overlay ? location.pathname : `/crm/persons/${id}`, scrollY: window.scrollY, open360PersonId: overlay ? id : id } };

  const CwRow = ({ label, children, testid }) => (
    <div className="flex items-center justify-between gap-3 py-1.5 border-b border-white/[0.04] last:border-0" data-testid={testid}>
      <span className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono shrink-0">{label}</span>
      <span className="text-right min-w-0">{children}</span>
    </div>
  );

  return (
    <Wrap {...wrapProps}>
    <div className={`relative space-y-5 ${overlay ? "px-1" : ""}`} data-testid="crm-person-360">
      {/* Back link (page) / Close (overlay) */}
      <div className="flex items-center justify-between">
        {overlay ? (
          <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full ${isCustomer ? "bg-emerald-500/15 text-emerald-300" : "bg-sky-500/15 text-sky-300"}`}>
            {isCustomer ? "● Customer" : "○ Lead"}
          </span>
        ) : (
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-white font-mono uppercase tracking-widest"
            data-testid="crm360-back"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> {returnTo?.route ? "Back" : "Back to customers"}
          </button>
        )}
        {overlay && onClose && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openFullProfile}
              className="text-[10px] font-mono uppercase tracking-widest text-slate-300 hover:text-white inline-flex items-center gap-1"
              data-testid="crm360-open-full"
            >
              Open full profile <ExternalLink className="w-3 h-3" />
            </button>
            <button type="button" onClick={onClose} className="text-slate-400 hover:text-white" data-testid="crm360-overlay-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* TWO-COLUMN HEADER — identity (left) · CW commercial (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4" data-testid="crm360-header">
        {/* Left — identity */}
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full grid place-items-center bg-gradient-to-br from-orange-500 to-fuchsia-600 text-white font-display font-bold text-lg shadow-lg shadow-orange-500/25 shrink-0"
            data-testid="crm360-avatar">{initials}</div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-orange-400 font-mono mb-1 flex items-center gap-1.5">
              <Users2 className="w-3 h-3" /> Customer 360{overlay ? " v2" : ""}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-display font-bold tracking-tight truncate" data-testid="crm360-name">
                {person.full_name || "Unknown contact"}
              </h1>
              {can("edit") && <button
                type="button"
                onClick={openHdrEdit}
                className="text-slate-400 hover:text-orange-300 shrink-0"
                title="Edit name & details"
                data-testid="crm360-edit-name"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>}
              <TierChip tier={person.vip_tier} />
              <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${isCustomer ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25" : "bg-sky-500/15 text-sky-300 border border-sky-500/25"}`}
                data-testid="crm360-lifecycle-badge">{isCustomer ? "● Customer" : "○ Lead"}</span>
              <PipedriveIdChip
                pipelineId={person.pipeline_id ?? person.PipelineId}
                stageId={person.stage_id ?? person.StageId}
                shortVip={person.short_vip_level ?? person.ShortVIPLevel}
                testid="crm360-pd-chip"
              />
            </div>
            <div className="mt-2 flex items-center gap-2" data-testid="crm360-export">
              <span className="text-[9.5px] font-mono uppercase tracking-widest text-slate-500">Export</span>
              <a href={`${API}/crm/persons/${id}/export.csv?level=basic`}
                className="text-[11px] px-2 py-0.5 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 inline-flex items-center gap-1"
                data-testid="crm360-export-basic"><Download className="w-3 h-3" /> Basic CSV</a>
              <a href={`${API}/crm/persons/${id}/export.csv?level=advanced`}
                className="text-[11px] px-2 py-0.5 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 inline-flex items-center gap-1"
                data-testid="crm360-export-advanced"><Download className="w-3 h-3" /> Advanced ZIP</a>
            </div>
            {primaryCompany && (
              <div className="text-slate-400 mt-1 flex items-center gap-1.5 text-sm" data-testid="crm360-company">
                <Building2 className="w-3.5 h-3.5" /> {primaryCompany}
              </div>
            )}
            <div className="mt-2 flex flex-col gap-1 text-sm">
              {person.phone && (
                <a href={`tel:${person.phone}`} className="inline-flex items-center gap-1.5 text-slate-300 hover:text-white" data-testid="crm360-phone">
                  <Phone className="w-3.5 h-3.5 text-sky-300" />{person.phone}
                </a>
              )}
              {person.email && (
                <a href={`mailto:${person.email}`} className="inline-flex items-center gap-1.5 text-slate-300 hover:text-white" data-testid="crm360-email">
                  <Mail className="w-3.5 h-3.5 text-orange-300" />{person.email}
                </a>
              )}
              {address && (
                <span className="inline-flex items-start gap-1.5 text-slate-400" data-testid="crm360-address">
                  <MapPin className="w-3.5 h-3.5 text-emerald-300 mt-0.5" />{address}
                </span>
              )}
            </div>
            {editHdr && hdrForm && (
              <div className="mt-3 p-3 rounded-lg border border-white/10 bg-white/[0.03] space-y-2" data-testid="crm360-name-edit">
                <div className="grid grid-cols-2 gap-2">
                  <input className="input-dark py-1.5 text-sm" placeholder="First name" value={hdrForm.first_name}
                    onChange={(e) => setHdrForm({ ...hdrForm, first_name: e.target.value })} data-testid="crm360-edit-first" />
                  <input className="input-dark py-1.5 text-sm" placeholder="Last name" value={hdrForm.last_name}
                    onChange={(e) => setHdrForm({ ...hdrForm, last_name: e.target.value })} data-testid="crm360-edit-last" />
                </div>
                <input className="input-dark py-1.5 text-sm w-full" placeholder="Company" value={hdrForm.company}
                  onChange={(e) => setHdrForm({ ...hdrForm, company: e.target.value })} data-testid="crm360-edit-company" />
                <div className="grid grid-cols-2 gap-2">
                  <input className="input-dark py-1.5 text-sm" placeholder="Email" value={hdrForm.email}
                    onChange={(e) => setHdrForm({ ...hdrForm, email: e.target.value })} data-testid="crm360-edit-email" />
                  <input className="input-dark py-1.5 text-sm" placeholder="Phone" value={hdrForm.phone}
                    onChange={(e) => setHdrForm({ ...hdrForm, phone: e.target.value })} data-testid="crm360-edit-phone" />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={() => setEditHdr(false)} className="px-3 py-1.5 rounded-md text-xs border border-white/10 hover:bg-white/5">Cancel</button>
                  <button type="button" onClick={saveHdr} disabled={savingHdr} className="px-3 py-1.5 rounded-md text-xs bg-orange-500 hover:bg-orange-600 text-white font-medium" data-testid="crm360-edit-save">
                    {savingHdr ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right — CW commercial panel */}
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3.5" data-testid="crm360-cw-panel">
          <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-mono mb-2 flex items-center gap-1.5">
            <Briefcase className="w-3 h-3 text-orange-300" /> Construction Warehouse
          </div>
          <div className="space-y-0.5">
            <CwRow label="Account manager" testid="crm360-cw-am">
              {can("edit") ? <OwnerAssignChip
                personId={id}
                primary={person.primary_am}
                size="sm"
                onChanged={load}
                changed={(person.am_history || [])[0]}
              /> : <span className="text-[12px] text-slate-200" data-testid="crm360-am-readonly">{person.primary_am || "—"}</span>}
            </CwRow>
            <CwRow label="Collaborators" testid="crm360-cw-collaborators">
              <CwCollaborators personId={id} />
            </CwRow>
            {person.vip_tier && (
              <CwRow label="Tier" testid="crm360-cw-tier"><TierChip tier={person.vip_tier} /></CwRow>
            )}
            {isCustomer ? (
              <>
                <CwRow label="Lifetime value" testid="crm360-cw-ltv">
                  <span className="text-emerald-300 font-semibold tabular-nums">{money(person.ltv)}</span>
                </CwRow>
                <CwRow label="Orders" testid="crm360-cw-orders">
                  <span className="text-white font-semibold tabular-nums">{person.orders_count ?? 0}</span>
                  <span className="text-[10px] text-slate-500 font-mono ml-1">{person.reg_orders ?? 0} reg · {person.guest_orders ?? 0} guest</span>
                  {(person.orders_on_record ?? 0) > 0 && (
                    <span
                      data-testid="crm360-cw-orders-availability"
                      title={((person.orders_available ?? 0) === 0 && (person.orders_on_record ?? 0) > 0)
                        ? "Order records for this customer are not present in this environment" : undefined}
                      className={`block text-[10px] font-mono mt-0.5 ${
                        ((person.orders_available ?? 0) === 0 && (person.orders_on_record ?? 0) > 0)
                          ? "text-amber-400" : "text-slate-500"}`}>
                      {person.orders_available ?? 0} of {person.orders_on_record} available in timeline
                    </span>
                  )}
                </CwRow>
                <CwRow label="Trade Account" testid="crm360-cw-trade-account">
                  <TradeAccountBadge ta={person.trade_account} testid="crm360-trade-badge" />
                </CwRow>
                <CwRow label="Last order" testid="crm360-cw-last-order">
                  <span className="text-slate-200 text-[12px]">{fmtDay(person.last_order_date)}</span>
                  <span className="text-[10px] text-slate-500 font-mono ml-1">{relTime(person.last_order_date)}</span>
                </CwRow>
              </>
            ) : (
              <>
                <CwRow label="Open quote value" testid="crm360-cw-open-quotes">
                  <span className="text-emerald-300 font-semibold tabular-nums">{money(openQuoteValue)}</span>
                </CwRow>
                <CwRow label="Engagement" testid="crm360-cw-engagement">
                  <span className="text-white font-semibold tabular-nums">{(calls || []).length}</span>
                  <span className="text-[10px] text-slate-500 font-mono ml-1">calls</span>
                </CwRow>
                <CwRow label="Added" testid="crm360-cw-added">
                  <span className="text-slate-200 text-[12px]">{fmtDay(person.created_date)}</span>
                </CwRow>
              </>
            )}
            {can("edit") && <div className="flex items-center justify-end gap-1.5 pt-2">
              <button type="button" onClick={runPdSync} disabled={syncing} data-testid="crm360-pd-sync"
                title="Recompute tier and stage from Mongo PipelineId + StageId + ShortVIPLevel."
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-orange-500/40 bg-orange-500/[0.06] text-[10px] font-mono uppercase tracking-widest text-orange-200 hover:bg-orange-500/[0.14] disabled:opacity-50">
                {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} PD Sync
              </button>
              <SyncButton resource="person" targetId={person.PersonId || person.person_id || id} variant="subtle" label="Sync T1" />
              <SyncButton resource="email" variant="subtle" label="Sync Email" />
            </div>}
          </div>
        </div>
      </div>

      {/* PROFILE — static facts, permanently expanded (AM + collaborators now live up top) */}
      <div className="space-y-2">
        <h2 className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">Profile</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start" data-testid="crm360-summary-shelf">
          <ContactChannelsCard person={person} personId={id} onSaved={load} openSignal={{ value: true, ts: 1 }} />
          <RailSection title="Billing & delivery addresses" testId="crm360-rail-addresses" defaultOpen highlight>
            <AddressBookPanel book={addrBook} readOnly={!can("edit")} />
          </RailSection>
          <CompanyCreditPanel person={person} personId={id} onSaved={load} />
          <AMTouchesCard personId={id} />
          <OldQuotesCard personId={id} onRestored={load} />
          {person.trade_account && <TradeAccountCard key={`${id}-${person.trade_account.status}`} personId={id} ta={person.trade_account} onChanged={load} />}
        </div>
      </div>

      {/* DEAL STAGE */}
      <DealStageStrip currentStageKey={person.stage_key} />

      {/* OPEN WORK — activities, notes & actions (unified composer) + AI brief */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-3 items-start">
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3.5" data-testid="crm360-open-work">
          <div className="flex items-center justify-between mb-2.5">
            <div className="text-[10px] uppercase tracking-widest text-orange-300 font-mono flex items-center gap-1.5">
              <Briefcase className="w-3 h-3" /> Open work
              <span className="text-slate-500" data-testid="crm360-open-work-count">· {wsTasks.length + (data.talmia_activities || []).filter((a) => (a.status || (a.completed ? "done" : "open")) === "open").length} open</span>
            </div>
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => setShowDoneWork((v) => !v)} data-testid="crm360-openwork-toggle-done"
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10.5px] font-medium border transition-colors ${showDoneWork ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-200" : "border-white/10 text-slate-400 hover:text-white"}`}>
                <Check className="w-3 h-3" /> {showDoneWork ? "Hide completed" : "Show completed"}
              </button>
              <button type="button" onClick={() => setModal("activity")} data-testid="crm360-openwork-log-activity"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium border border-purple-500/40 bg-purple-500/10 text-purple-200 hover:bg-purple-500/20">
                <Plus className="w-3 h-3" /> Log activity
              </button>
              {can("edit") && <button type="button" onClick={() => setModal("note")} data-testid="crm360-openwork-add-note"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium border border-orange-500/40 bg-orange-500/10 text-orange-200 hover:bg-orange-500/20">
                <Plus className="w-3 h-3" /> Add note
              </button>}
            </div>
          </div>
          <div className="space-y-1">
            {(() => {
              const all = data.talmia_activities || [];
              const statusOf = (a) => a.status || (a.completed ? "done" : "open");
              const openActs = all.filter((a) => statusOf(a) === "open");
              const doneActs = all.filter((a) => statusOf(a) !== "open");
              const today = new Date().toISOString().slice(0, 10);
              const STBADGE = {
                done: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
                cancelled: "bg-slate-500/15 text-slate-400 border-slate-500/40",
              };
              const patchStatus = async (aid, status) => {
                await axios.patch(`${API}/crm/activities/${aid}`, { status }, { withCredentials: true });
                await load(); loadWorkspaceTasks();
              };
              const Row = ({ a }) => {
                const st = statusOf(a);
                const overdue = st === "open" && a.due_date && String(a.due_date) < today;
                return (
                  <div key={a._id} data-testid={`crm360-act-${a._id}`}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded border ${st === "open" ? "border-purple-500/15 bg-purple-500/[0.04]" : "border-white/[0.06] bg-white/[0.015] opacity-75"}`}>
                    <span className="text-[8.5px] font-mono uppercase tracking-widest text-purple-300/80 shrink-0">{a.activity_type}</span>
                    <span className={`text-[12px] truncate flex-1 ${st === "cancelled" ? "line-through text-slate-500" : "text-slate-200"}`}>{a.subject}</span>
                    {st !== "open" && <span className={`text-[8.5px] font-mono uppercase px-1.5 py-0.5 rounded-full border shrink-0 ${STBADGE[st]}`}>{st}</span>}
                    {a.due_date && st === "open" && <span className={`text-[10px] font-mono shrink-0 ${overdue ? "text-rose-300" : "text-amber-300/80"}`}>{overdue ? "Overdue " : ""}{fmtDay(a.due_date)}</span>}
                    <button type="button" onClick={() => setEditAct({ id: a._id, person_id: id, activity_type: a.activity_type, subject: a.subject, content: a.content, due_date: a.due_date, priority: a.priority, assignee_id: a.assignee_id, status: st, completed: a.completed, details: a.details, quote_id: a.quote_id })}
                      data-testid={`crm360-act-edit-${a._id}`} title="Edit"
                      className="p-1 rounded border border-white/10 bg-white/[0.03] hover:border-white/25 text-slate-300 shrink-0"><Pencil className="w-3 h-3" /></button>
                    {st === "open" ? (
                      <>
                        <button type="button" onClick={() => patchStatus(a._id, "done")}
                          data-testid={`crm360-act-done-${a._id}`} title="Mark done"
                          className="p-1 rounded border border-white/10 bg-white/[0.03] hover:border-emerald-400/50 text-emerald-300 shrink-0"><Check className="w-3 h-3" /></button>
                        <button type="button" onClick={() => patchStatus(a._id, "cancelled")}
                          data-testid={`crm360-act-cancel-${a._id}`} title="Cancel"
                          className="p-1 rounded border border-white/10 bg-white/[0.03] hover:border-rose-400/50 text-slate-400 shrink-0"><X className="w-3 h-3" /></button>
                      </>
                    ) : (
                      <button type="button" onClick={() => patchStatus(a._id, "open")}
                        data-testid={`crm360-act-reopen-${a._id}`} title="Reopen"
                        className="p-1 rounded border border-white/10 bg-white/[0.03] hover:border-amber-400/50 text-amber-300 shrink-0"><RotateCcw className="w-3 h-3" /></button>
                    )}
                  </div>
                );
              };
              const showList = showDoneWork ? [...openActs, ...doneActs] : openActs;
              if (showList.length === 0) return null;
              return (
                <div className="mb-2 space-y-1" data-testid="crm360-open-activities">
                  {showList.slice(0, 20).map((a) => <Row key={a._id} a={a} />)}
                  {!showDoneWork && doneActs.length > 0 && (
                    <button type="button" onClick={() => setShowDoneWork(true)} data-testid="crm360-openwork-show-done-inline"
                      className="text-[10.5px] text-slate-500 hover:text-slate-300 pl-1 pt-0.5">+ {doneActs.length} completed/cancelled</button>
                  )}
                </div>
              );
            })()}
            {wsTasks.length === 0 && !(data.talmia_activities || []).some((a) => (a.status || (a.completed ? "done" : "open")) === "open") && (
              <div className="text-slate-500 text-[12px] italic px-1 py-3 text-center" data-testid="crm360-open-work-empty">
                No open work. Log a call, follow-up or note to schedule your next action.
              </div>
            )}
            {wsTasks.slice(0, 12).map((t) => (
              <a key={t.id} href={`/workspace/my-tasks?open=${t.id}`} data-testid={`crm360-ws-task-${t.id}`}
                className="flex items-center gap-2 px-2 py-1.5 rounded border border-white/[0.05] bg-white/[0.015] hover:bg-white/[0.04]">
                <span className="text-[8.5px] font-mono uppercase tracking-widest text-slate-500 shrink-0">{t.status}</span>
                <span className="text-[12px] text-slate-200 truncate flex-1">{t.title}</span>
                {t.due_date && <span className="text-[10px] font-mono text-amber-300/80 shrink-0">{fmtDay(t.due_date)}</span>}
                <span className="text-[10px] font-mono text-slate-500 shrink-0">{t.priority}</span>
              </a>
            ))}
          </div>
          {/* Attachments — de-emphasised (often empty), tucked away at the foot */}
          <div className="mt-2 pt-2 border-t border-white/[0.05]">
            <RailSection title="Attachments" testId="crm360-rail-attachments" count={mediaCount != null ? mediaCount : (person.attachments || []).length} defaultOpen={false} peek="Docs & files">
              <div className="p-2">
                <MediaManager contextType="crm" entityId={id} title="Attachments" subtitle="Docs & files for this contact" />
              </div>
            </RailSection>
          </div>
        </div>
        <AiSummaryBox personId={id} />
      </div>


      {/* FULL-WIDTH TIMELINE */}
      <div ref={timelineRef} className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-display font-semibold">Timeline</h2>
          <div className="flex items-center gap-2">
            {can("edit") && <button type="button" onClick={() => setModal("activity")} data-testid="crm360-add-activity"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium border border-purple-500/40 bg-purple-500/10 text-purple-200 hover:bg-purple-500/20 transition-colors">
              <Plus className="w-3 h-3" /> Log activity
            </button>}
            {can("edit") && <button type="button" onClick={() => setModal("note")} data-testid="crm360-add-note"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium border border-orange-500/40 bg-orange-500/10 text-orange-200 hover:bg-orange-500/20 transition-colors">
              <Plus className="w-3 h-3" /> Add note
            </button>}
            <div className="flex items-center gap-1 ml-1">
              <button type="button" onClick={() => setTlEventSignal({ value: true, ts: Date.now() })}
                data-testid="crm360-timeline-expand-all"
                className="text-[10.5px] font-mono text-slate-400 hover:text-white px-2 py-0.5 rounded hover:bg-white/5">Expand all</button>
              <button type="button" onClick={() => setTlEventSignal({ value: false, ts: Date.now() })}
                data-testid="crm360-timeline-collapse-all"
                className="text-[10.5px] font-mono text-slate-400 hover:text-white px-2 py-0.5 rounded hover:bg-white/5">Collapse all</button>
            </div>
            <span className="text-[11px] text-slate-500 font-mono ml-1">
              {filteredTimeline.length}{tlFilter !== "all" ? ` / ${timeline.length}` : ""} events
            </span>
          </div>
        </div>

        {/* Single coloured channel chip row (registry-driven; 0-count hidden).
            Each chip carries its channel colour; active = brighter + ring. */}
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="crm360-timeline-filters">
          <button type="button" onClick={() => setTlFilter("all")} data-testid="crm360-tlchip-all"
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${tlFilter === "all" ? "bg-white/20 text-white border-white/30 ring-1 ring-white/20" : "border-white/10 text-slate-300 hover:text-white hover:bg-white/[0.06]"}`}>
            All <span className="tabular-nums opacity-70">{timeline.length}</span>
          </button>
          {TL_CHANNELS.filter((ch) => (tlCounts[ch.key] || 0) > 0 || (ch.key === "order" && isCustomer)).map((ch) => {
            const count = tlCounts[ch.key] || 0;
            const dimmed = count === 0;  // customer Orders chip with nothing displayable
            const active = tlFilter === ch.key;
            const meta = TYPE_META[ch.key] || TYPE_META.note;
            const Icon = meta.icon;
            return (
              <button key={ch.key} type="button" onClick={() => setTlFilter(active ? "all" : ch.key)} data-testid={`crm360-tlchip-${ch.key}`}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${meta.bg} ${meta.iconColor} ${
                  dimmed ? "opacity-40 border-white/10" : active ? `border-white/25 ring-1 ${meta.ring} brightness-125` : "border-white/10 opacity-75 hover:opacity-100"
                }`}>
                {Icon && <Icon className="w-3 h-3" />}
                {ch.label} <span className="tabular-nums opacity-80">{count}</span>
              </button>
            );
          })}
        </div>

        <div className="rounded-lg border border-white/[0.06] bg-white/[0.015] p-4 space-y-4" data-testid="crm360-timeline">
          {(person.orders_on_record ?? 0) > 0 && (person.orders_available ?? 0) === 0 && (
            <div className="text-[12px] text-amber-400 font-mono" data-testid="crm360-orders-unavailable">
              {person.orders_on_record} orders on record, none available to display here.
            </div>
          )}
          {filteredTimeline.length === 0 && (
            <div className="text-center py-8 text-slate-500 text-sm">
              {timeline.length === 0 ? "No timeline events yet." : "No events in this channel."}
            </div>
          )}
          {(() => {
            const totalEmails = filteredTimeline.filter((e) => e.type === "email").length;
            let emailSeen = 0;
            const rows = [];
            for (const ev of filteredTimeline) {
              if (ev.type === "email") {
                emailSeen += 1;
                if (emailSeen <= 5) {
                  rows.push(<EmailTimelineRow key={ev.key} ev={ev} onOpen={(e) => setEmailThread({ highlightId: e.emailId })} />);
                } else if (emailSeen === 6) {
                  rows.push(<EmailMoreRow key="email-more" count={totalEmails - 5} onOpen={() => setEmailThread({ highlightId: null })} />);
                }
                continue; // 7th+ emails folded into the single "N more" row
              }
              rows.push(
                <TimelineEvent
                  key={ev.key}
                  ev={ev}
                  openSignal={tlEventSignal}
                  onDrill={(e) => (e.type === "order" ? setOrderDrill(e.linkTo?.split("/").pop())
                    : e.type === "quote" ? setPeekQuoteId(e.linkTo?.split("/")[2] || null)
                    : setDrill(e))}
                  onViewWhatsApp={(e) => setWaThread({ phoneKey: e.phoneKey, highlightId: e.messageId, contactName: person.full_name })}
                  onActivityStatus={async (aid, status) => { await axios.patch(`${API}/crm/activities/${aid}`, { status }, { withCredentials: true }); await load(); loadWorkspaceTasks(); }}
                />
              );
            }
            return rows;
          })()}
        </div>

        {/* FULL DATA RECORD — bottom of timeline, admin/management only */}
        <FullDataRecord raw={raw_record} aiEnrichment={ai_enrichment} canView={can_view_raw} />
      </div>

      {/* Drill-in drawer — layered over the 360 so Back returns to exact position */}
      {drill && <DrillInDrawer ev={drill} onBack={() => setDrill(null)} returnState={drillReturnState} />}

      {/* WhatsApp conversation thread — contact-level, opened from a WhatsApp timeline event */}
      {waThread && (
        <WhatsAppThreadDrawer
          phoneKey={waThread.phoneKey}
          highlightId={waThread.highlightId}
          contactName={waThread.contactName}
          onBack={() => setWaThread(null)}
        />
      )}

      {/* Email conversation thread — all of this person's emails as chat bubbles */}
      {emailThread && (
        <EmailThreadDrawer
          emails={data.emails}
          highlightId={emailThread.highlightId}
          contactName={person.full_name}
          onBack={() => setEmailThread(null)}
        />
      )}

      {/* Order detail — the ONE reusable modal (05a §2); Back returns to this 360 */}
      {orderDrill && (
        <OrderDetailModal
          orderId={orderDrill}
          onClose={() => setOrderDrill(null)}
          onOpenAI={() => {}}
          context={{ label: `Customer 360 · ${person.full_name || "Contact"}`, returnTo: () => {} }}
        />
      )}

      {/* Quote peek — central modal (Pass 2); replaces the timeline drill drawer for quotes */}
      <QuotePeekModal quoteId={peekQuoteId} open={!!peekQuoteId} onClose={() => setPeekQuoteId(null)} />

      {/* Add-activity / Add-note modal */}
      {modal && (
        <ActivityLogModal
          kind={modal}
          personId={id}
          defaultAssigneeName={person.primary_am}
          contextLabel={person.full_name}
          onClose={() => setModal(null)}
          onSaved={async () => { setModal(null); await load(); loadMediaCount(); loadWorkspaceTasks(); }}
        />
      )}
      {editAct && (
        <ActivityLogModal
          personId={id}
          activity={editAct}
          contextLabel={person.full_name}
          defaultAssigneeName={person.primary_am}
          onClose={() => setEditAct(null)}
          onSaved={async () => { setEditAct(null); await load(); loadWorkspaceTasks(); }}
        />
      )}
    </div>
    </Wrap>
  );
}

function IdRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  const copy = () => navigator.clipboard?.writeText(String(value));
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-300 truncate flex items-center gap-1 max-w-[65%]">
        <span className="truncate">{value}</span>
        <button type="button" onClick={copy} className="text-slate-500 hover:text-white shrink-0" title="Copy">
          <Copy className="w-3 h-3" />
        </button>
      </span>
    </div>
  );
}
